import { useState, useEffect, useRef } from 'react';
import { Link,useNavigate,useLocation } from 'react-router-dom';
import { Building2, ShoppingCart, TrendingUp, Shield, Users, ArrowRight, Phone, Mail, MapPin, Star, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { tokenStorage } from '@/user/api'; // 【新增】引入 tokenStorage

const stats = [
  { value: '5000+', label: '入驻企业' },
  { value: '100亿+', label: '年交易额' },
  { value: '98%', label: '满意度' },
  { value: '24h', label: '响应速度' },
];

const supplierBenefits = [
  {
    icon: TrendingUp,
    title: '海量采购需求',
    description: '连接5000+采购商，获取稳定订单来源',
  },
  {
    icon: Shield,
    title: '交易安全保障',
    description: '平台担保交易，资金安全可靠',
  },
  {
    icon: Users,
    title: '精准客户匹配',
    description: '智能推荐系统，精准匹配目标客户',
  },
  {
    icon: Star,
    title: '品牌推广支持',
    description: '平台流量扶持，提升品牌知名度',
  },
];

const buyerBenefits = [
  {
    icon: Building2,
    title: '优质供应商',
    description: '严选优质供应商，品质有保障',
  },
  {
    icon: TrendingUp,
    title: '价格透明',
    description: '公开透明的价格体系，采购更省心',
  },
  {
    icon: Shield,
    title: '质量保障',
    description: '严格质量把控，不合格包退换',
  },
  {
    icon: Users,
    title: '专属服务',
    description: '一对一客户经理，全程跟进服务',
  },
];

const categories = [
  { name: '钢材', icon: '🔩', count: '1200+供应商' },
  { name: '有色金属', icon: '⚙️', count: '800+供应商' },
  { name: '化工原料', icon: '🧪', count: '600+供应商' },
  { name: '建材', icon: '🏗️', count: '900+供应商' },
  { name: '机械设备', icon: '🔧', count: '500+供应商' },
  { name: '电子产品', icon: '💻', count: '700+供应商' },
  { name: '农产品', icon: '🌾', count: '400+供应商' },
  { name: '能源', icon: '⚡', count: '300+供应商' },
];

const processSteps = [
  { step: 1, title: '提交申请', desc: '填写企业信息' },
  { step: 2, title: '资质审核', desc: '3-5个工作日' },
  { step: 3, title: '签约入驻', desc: '签署合作协议' },
  { step: 4, title: '开通账号', desc: '开始交易' },
];

const successCases = [
  {
    name: '华鑫钢铁集团',
    type: '供应商',
    result: '入驻6个月，订单量增长300%',
    avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face',
  },
  {
    name: '中建三局',
    type: '采购商',
    result: '年采购成本降低15%',
    avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face',
  },
  {
    name: '东方铝业',
    type: '供应商',
    result: '月均交易额突破500万',
    avatar: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=100&h=100&fit=crop&crop=face',
  },
];

export default function Investment() {
  const [activeTab, setActiveTab] = useState<'supplier' | 'buyer'>('supplier');
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handSubmit = (type: 'supplier' | 'buyer') => () => {
    // 1. 判断是否登录
    if (!tokenStorage.isLoggedIn()) {
      // 2. 未登录：跳转到登录页
      // 只传递简单的字符串路径，不要传递整个 location 对象
      navigate('/login', { 
        state: { 
          from: location.pathname, // 只传递路径字符串
          targetPath: '/console/profile',
          targetState: type 
        } 
      });
      return;
    }
    navigate('/console/profile', { 
      state: type
    });
  }
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative h-[500px] md:h-[600px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=800&fit=crop)` }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1c5bff]/90 to-[#1c5bff]/70" />
          <div className="relative h-full section-container flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                大宗商品交易平台
              </h1>
              <p className="text-xl md:text-2xl text-white/90 mb-8">
                诚邀优质供应商、采购商入驻，共建数字化供应链生态
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={handSubmit('supplier')} className="px-8 py-4 bg-white text-[#1c5bff] font-semibold rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
                  <Building2 className="w-5 h-5" />
                  供应商入驻
                </button>
                <button onClick={handSubmit('buyer')} className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  采购商入驻
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="section-container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold text-[#1c5bff] mb-2">{stat.value}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">热门品类</h2>
              <p className="text-gray-600">覆盖多个行业领域，满足多样化采购需求</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map((cat) => (
                <div
                  key={cat.name}
                  className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className="text-4xl mb-3">{cat.icon}</div>
                  <h3 className="font-bold text-gray-900 mb-1 group-hover:text-[#1c5bff] transition-colors">{cat.name}</h3>
                  <p className="text-sm text-gray-500">{cat.count}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Tabs */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">入驻权益</h2>
              <p className="text-gray-600">无论您是供应商还是采购商，都能获得专属权益</p>
            </div>

            {/* Tabs */}
            <div className="flex justify-center mb-12">
              <div className="inline-flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('supplier')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    activeTab === 'supplier'
                      ? 'bg-[#1c5bff] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  供应商权益
                </button>
                <button
                  onClick={() => setActiveTab('buyer')}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    activeTab === 'buyer'
                      ? 'bg-[#1c5bff] text-white'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  采购商权益
                </button>
              </div>
            </div>

            {/* Benefits Content */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {(activeTab === 'supplier' ? supplierBenefits : buyerBenefits).map((benefit) => (
                <div key={benefit.title} className="text-center p-6">
                  <div className="w-16 h-16 bg-[#1c5bff]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-[#1c5bff]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-gray-50">
          <div className="section-container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">入驻流程</h2>
              <p className="text-gray-600">简单四步，快速入驻平台</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="relative">
                <div className="absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-gray-200 hidden md:block" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                  {processSteps.map((step) => (
                    <div key={step.step} className="text-center relative">
                      <div className="w-16 h-16 bg-[#1c5bff] rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                        <span className="text-2xl font-bold text-white">{step.step}</span>
                      </div>
                      <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
                      <p className="text-sm text-gray-500">{step.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Success Cases */}
        <section className="py-20 bg-white">
          <div className="section-container">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">成功案例</h2>
              <p className="text-gray-600">看看他们如何通过平台实现业务增长</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {successCases.map((item) => (
                <div key={item.name} className="bg-gray-50 rounded-2xl p-8 text-center">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="font-bold text-gray-900 mb-1">{item.name}</h3>
                  <span className="inline-block px-3 py-1 bg-[#1c5bff]/10 text-[#1c5bff] text-sm rounded-full mb-4">
                    {item.type}
                  </span>
                  <p className="text-gray-600">{item.result}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-[#1c5bff]">
          <div className="section-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  立即入驻，开启数字化交易新篇章
                </h2>
                <p className="text-xl text-white/80 mb-8">
                  加入互联网生态平台，与5000+企业共建数字化供应链生态
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <button onClick={handSubmit('buyer')} className="px-8 py-4 bg-white text-[#1c5bff] font-semibold rounded-lg hover:bg-gray-100 transition-colors">
                    立即申请入驻
                  </button>
                  <button className="px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
                    联系商务咨询
                  </button>
                </div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-xl font-bold text-white mb-6">联系我们</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-white/90">
                    <Phone className="w-5 h-5" />
                    <span>028-85002901</span>
                  </div>
                  <div className="flex items-center gap-4 text-white/90">
                    <Mail className="w-5 h-5" />
                    <span>business@kongming.com</span>
                  </div>
                  <div className="flex items-start gap-4 text-white/90">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>四川省成都市武侯区九架车巷55号悦湖数园1栋</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
